import { Chain, Web3Environment, WidgetIntegrationPayload } from "@src/models";
import { getChains } from "@src/services";
import { createWebComponent } from "@src/utils/webComponents";
import { useCallback, useEffect, useState } from "react";
import { SwapperApp } from "./app";
import CSS from "./styles/global.css";
import { isServer } from "@src/utils";
import { IS_PRODUCTION_ENV } from "@src/config";
import { useShift4Api } from "@src/contexts/swapProvider/useShift4Api";

const TxWidget = ({
  integratorId,
  integrationType,
  chainIds,
  dstChainId,
  dstToken,
  dstTokenImage,
  customContractCalls = [],
  externalWalletAddress,
  desc,
  defaultWalletPicker,
  development = false,
  cta,
  styles,
}: WidgetIntegrationPayload) => {
  const [supportedChains, setSupportedChains] = useState<Chain[]>([]);
  const [isFetchingChains, setIsFetchingChains] = useState(false);
  const [isDirectOnRampSupported, setIsDirectOnRampSupported] = useState(false);
  const [isCheckingDirectOnRamp, setIsCheckingDirectOnRamp] = useState(false);

  const { getSupportedAssets } = useShift4Api();

  useEffect(() => {
    if (!integratorId) return;

    setIsFetchingChains(true);

    (async () => {
      const chains = (await getChains(integratorId, chainIds)).filter(
        ({ web3Environment }) =>
          !IS_PRODUCTION_ENV || web3Environment === Web3Environment.MAINNET,
      );

      setSupportedChains(chains);
      setIsFetchingChains(false);
    })();
  }, [chainIds, integratorId]);

  // Check if direct onramp is supported for the specified token
  const checkDirectOnRampSupport = useCallback(async () => {
    // Only check if we have all required params
    if (
      !dstChainId ||
      !dstToken ||
      !externalWalletAddress ||
      supportedChains.length === 0
    ) {
      return;
    }

    // Find the destination chain
    const targetChain = supportedChains.find(
      (chain) => chain.chainId === dstChainId,
    );
    if (!targetChain || !targetChain.shift4Pk) {
      return;
    }

    setIsCheckingDirectOnRamp(true);

    try {
      const response = await getSupportedAssets(targetChain);

      // Check if the destination token is supported
      const isSupported = response.data.assets.some(
        (asset) =>
          asset.contractAddress.toLowerCase() === dstToken.toLowerCase(),
      );

      setIsDirectOnRampSupported(isSupported);
    } catch (error) {
      console.error("Error checking direct onramp support:", error);
    } finally {
      setIsCheckingDirectOnRamp(false);
    }
  }, [
    dstChainId,
    dstToken,
    externalWalletAddress,
    supportedChains,
    getSupportedAssets,
  ]);

  useEffect(() => {
    checkDirectOnRampSupport();
  }, [checkDirectOnRampSupport]);

  // INIT SHIFT4 SDK
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/javascript";
    script.src = IS_PRODUCTION_ENV
      ? "https://crypto.shift4.com/sdk/v1/shift4crypto-sdk-latest.js"
      : "https://sandbox.crypto.shift4.com/sdk/v1/shift4crypto-sdk-latest.js";
    script.id = "shift4-sdk-script";
    document.head.appendChild(script);

    return () => {
      // Clean up script on unmount
      const existingScript = document.getElementById("shift4-sdk-script");
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <>
      <style>{CSS}</style>

      <SwapperApp
        integratorId={integratorId}
        integrationType={integrationType}
        chainIds={chainIds}
        dstChainId={dstChainId}
        dstTokenAddr={dstToken}
        dstTokenImage={dstTokenImage}
        customContractCalls={customContractCalls}
        externalWalletAddress={externalWalletAddress}
        desc={desc}
        supportedChains={supportedChains}
        isFetchingChains={isFetchingChains}
        overlay={false}
        defaultWalletPicker={defaultWalletPicker}
        development={development}
        cta={cta}
        styles={styles}
        isDirectOnRampSupported={isDirectOnRampSupported}
        isCheckingDirectOnRamp={isCheckingDirectOnRamp}
      />
    </>
  );
};

/**
 * Web component uses the same names for attributes as {@link TxWidget} for props, but written in kebab-case.
 *
 * Attributes that are `object` or `array` must be provided as stringified JSON.
 *
 * For the reference, see {@link WidgetIntegrationPayload}.
 */

export const createTxWidgetWC = () => {
  if (isServer || typeof customElements === "undefined") {
    return {} as CustomElementConstructor;
  }

  const TxWidgetWC = createWebComponent(TxWidget, {
    shadow: "open",
    props: {
      integratorId: "string",
      integrationType: "string",
      chainIds: "json",
      dstChainId: "string",
      dstToken: "string",
      dstTokenImage: "string",
      customContractCalls: "json",
      externalWalletAddress: "string",
      desc: "string",
      defaultWalletPicker: "boolean",
      development: "boolean",
      cta: "json",
      styles: "json",
    },
  });

  TxWidgetWC.prototype.connectedCallback = function () {
    this.id = "swapper-sdk-widget";
  };

  return TxWidgetWC;
};

export type TxWidgetWCAttributes = {
  "integrator-id": WidgetIntegrationPayload["integratorId"];
  "integration-type": WidgetIntegrationPayload["integrationType"];
  "chain-ids": string;
  "dst-chain-id": WidgetIntegrationPayload["dstChainId"];
  "dst-token": WidgetIntegrationPayload["dstToken"];
  "dst-token-image": WidgetIntegrationPayload["dstTokenImage"];
  "custom-contract-calls": WidgetIntegrationPayload["customContractCalls"];
  desc: WidgetIntegrationPayload["desc"];
  "default-wallet-picker": WidgetIntegrationPayload["defaultWalletPicker"];
  development: WidgetIntegrationPayload["development"];
  cta: string | undefined; // JSON string representation of SwapperCTAs
  styles: string | undefined; // JSON string representation of SwapperStyles
};
